import { Viewport } from '@/enums/viewport';
import { ActionTree, MutationTree } from 'vuex';

const fromEntries = require('object.fromentries');
if (!Object.fromEntries) {
	fromEntries.shim();
}

export type UiState = {
	ready: boolean;
	viewport: Viewport;
	viewportPixels: number;
	breakpoint: Viewport; // TODO Assuming this is Viewport even though getCurrentBreakpoint returns other things.
	breakpoints: Breakpoints;
	scrollbarSize: { width: number; height: number };
	showFilterBar: boolean;
	keyboardHeight: number;
	screenHeight: number;
	screenWidth: number;
	hasRetinaSupport: boolean;
};

let viewport: Viewport = Viewport.SMARTPHONE;
if (process.client) {
	viewport = calculateViewport();
}

// STATE
const state = (): UiState | null => {
	return process.client || PLATFORM.IS_SPA
		? ({
				// Defines whether constants and router had been loaded
				ready: false,
				viewport,
				viewportPixels: calculateViewportPixels(),
				breakpoint: getCurrentBreakpoint(),
				scrollbarSize: calculateScrollbarSize(),
				showFilterBar: true,
				keyboardHeight: 0,
				screenHeight: calculateScreenHeight(),
				screenWidth: calculateScreenWidth(),
				hasRetinaSupport: hasRetinaSupport(),
		  } as UiState)
		: null;
};

type UiGetter<T> = (state: UiState) => T;

export type UiGetters = {
	isMobile: UiGetter<boolean>;
	screenWidth: UiGetter<number>;
	screenHeight: UiGetter<number>;
};

export interface Breakpoints {
	'screen-xs-min': number;
	'screen-sm-min': number;
	'screen-md-min': number;
	'screen-lg-min': number;
}

export type UiStore = {
	state: UiState;
	getters: UiGetters;
};

// GETTERS
export const getters: UiGetters = {
	isMobile(state): boolean {
		return state?.viewport !== Viewport.DESKTOP;
	},
	screenWidth(state): number {
		return state.screenWidth;
	},
	screenHeight(state): number {
		return state.screenHeight;
	},
};

// ACTIONS
const actions: ActionTree<UiState, any> = {
	setShowFilterBar: ({ commit }, payload: boolean) => {
		commit('SET_SHOW_FILTER_BAR', payload);
	},
};

// MUTATIONS
const mutations: MutationTree<UiState> = {
	SET_SHOW_FILTER_BAR(state, payload: boolean) {
		state.showFilterBar = payload;
	},
	SET_VIEWPORT(state, viewport: Viewport) {
		state.viewport = viewport;
	},
	SET_KEYBOARD_HEIGHT(state, keyboardHeight: number) {
		state.keyboardHeight = keyboardHeight;
	},
};

function calculateViewport(): Viewport {
	const width = window.innerWidth;
	const viewports: { width: number; viewport: Viewport }[] = [
		{ width: 512, viewport: Viewport.SMARTPHONE },
		{ width: 960, viewport: Viewport.TABLET },
		// {width: null, viewport: Viewport.DESKTOP},
	];
	return viewports.reduce((root: Viewport, item) => {
		return root === Viewport.DESKTOP && width <= item.width ? item.viewport : root;
	}, Viewport.DESKTOP);
}

function calculateViewportPixels(): number {
	return window.innerWidth;
}

function calculateScreenHeight(): number {
	return window.screen.height;
}

function calculateScreenWidth(): number {
	return window.screen.width;
}

/**
 * Get the current breakpoint via body::after generated content from global.scss
 * @return {string} - 'smartphone' || 'tablet' || 'desktop'
 **/
function getCurrentBreakpoint(): string {
	// TODO We likely want to keep breakpoints somewhere in JS and also reactive (with debounce), not bound to CSS.
	// Current implementation doesn't seem to work properly (I got 'none' as a result of this function at app startup).
	return window.getComputedStyle(document.body, '::after').content.replace(/["\s]/g, '');
}

/**
 * Get all breakpoint keys and pixel values via html::after generated content from global.scss
 * @return {Breakpoints} - key/value pairs
 **/
export const BREAKPOINT_VALUES: Record<string, number> = {
	// @todo import $breakpoints variable from css
	'screen-xs-min': 480,
	'screen-sm-min': 768,
	'screen-md-min': 992,
	'screen-lg-min': 1200,
};

/**
 * We need this in order to hide the scrollbar later while still allowing the user to scroll with wheel and touch
 */
function calculateScrollbarSize() {
	const el = document.createElement('div');
	el.style.visibility = 'hidden';
	el.style.overflow = 'scroll';

	document.body.appendChild(el);
	const width = el.offsetWidth - el.clientWidth;
	const height = el.offsetHeight - el.clientHeight;
	document.body.removeChild(el);

	return {
		width,
		height,
	};
}

function hasRetinaSupport() {
	if (!window) {
		return true;
	}
	const matchString =
		'only screen and (-webkit-min-device-pixel-ratio: 2), only screen and (-o-min-device-pixel-ratio: 2/1), only screen and (min--moz-device-pixel-ratio: 2), only screen and (min-device-pixel-ratio: 2)';
	return !!(
		(window.matchMedia && window.matchMedia(matchString).matches) ||
		(window.devicePixelRatio && window.devicePixelRatio >= 2)
	);
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
